import { useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';

import { useActiveHexContext } from 'shared/context';
import { useHexGeneration, useHexAnalysis, useMouseCoords } from 'shared/hooks';
import { getHexOffset, featureToColor } from 'shared/utils';
import { HEX } from 'shared/constants';

type MiniHexFeature = {
  row: number;
  col: number;
  dominantFeature: string;
};

export const useMiniHex = () => {
  const {
    ctxHex: ctx,
    isBigHexViewActive,
    setMiniHexDominantFeature,
  } = useActiveHexContext();
  const { getFeaturesInHexPath } = useHexAnalysis();
  const { generateHexPaths } = useHexGeneration();
  const { mouseX, mouseY } = useMouseCoords();

  const [miniHexes, setMiniHexes] = useState<(string | null)[][]>([]);

  const miniHexFeatures = useMemo(async () => {
    if (!isBigHexViewActive) return [];
    const features: MiniHexFeature[] = [];
    for (const row in miniHexes) {
      for (const col in miniHexes) {
        const hex = miniHexes[row][col];
        if (hex) {
          const hexFeaturesWithEarth = await getFeaturesInHexPath(hex);
          const {
            earth,
            protected_area,
            recreation_ground,
            nature_reserve,
            ...hexFeatures
          } = hexFeaturesWithEarth;
          const sortedFeatures = orderBy(
            Object.entries(hexFeatures),
            (feature: any[]) => feature[1],
            ['desc'],
          ).map(feature => feature[0]);
          features.push({
            row: Number(row),
            col: Number(col),
            dominantFeature: sortedFeatures[0] ?? 'UNKNOWN',
          });
        }
      }
    }
    return features;
  }, [isBigHexViewActive]);

  const getMiniHexFeature = async (row: number, col: number) => {
    const hexDominantFeature =
      (await miniHexFeatures).find(
        (f: MiniHexFeature) => f.row === row && f.col === col,
      )?.dominantFeature ?? 'UNKNOWN';
    return hexDominantFeature;
  };

  const getFeatureColor = (feature: string) => {
    const color = featureToColor[feature] ?? featureToColor.UNKNOWN;
    return color;
  };

  const getMiniHexColor = async (row: number, col: number) => {
    const hexDominantFeature = await getMiniHexFeature(row, col);
    const color = getFeatureColor(hexDominantFeature);
    return color;
  };

  const drawMiniHex = async (
    hexPathStr: string | null,
    row: number,
    col: number,
  ) => {
    if (!ctx || !hexPathStr) return;
    const hexPath = new Path2D(hexPathStr);
    const isMiniHexHovered = ctx.isPointInPath(hexPath, mouseX, mouseY);
    const hexColor = (await miniHexFeatures)
      ? await getMiniHexColor(row, col)
      : featureToColor.UNKNOWN;

    ctx.lineWidth = 5;
    ctx.strokeStyle = 'black';
    ctx.fillStyle = isMiniHexHovered ? hexColor : `${hexColor}99`;
    ctx.fill(hexPath);
    ctx.stroke(hexPath);

    if (isMiniHexHovered) {
      const miniHexDominantFeature = await getMiniHexFeature(row, col);
      setMiniHexDominantFeature(miniHexDominantFeature);
    }
  };

  const drawMiniHexGrid = (hexPaths: (string | null)[][]) =>
    hexPaths.forEach((row, rowI) =>
      row.forEach((hex, colI) => drawMiniHex(hex, rowI, colI)),
    );

  const generateMiniHexGrid = (scale: number) => {
    if (!ctx) return;
    const MINI_HEX_MAP = MINI_HEX_MAP_5x5;
    const matrixSize = MINI_HEX_MAP.length;
    const modifier = 0.25;
    const miniHexWidth = (HEX.WIDTH * scale) / matrixSize;
    const offset = getHexOffset(ctx, HEX.WIDTH * scale);
    const miniHexPaths = MINI_HEX_MAP.map((row: number[], rowIndex: number) => {
      return row.map((col: number, colIndex: number) => {
        if (col === 0) return null;
        return generateHexPaths(
          { col: colIndex, row: rowIndex },
          {
            x: -offset.x / 2 - miniHexWidth / 2,
            y: -offset.y / 2 - (3 / 2) * miniHexWidth + modifier * miniHexWidth,
          },
          miniHexWidth,
        );
      });
    });
    setMiniHexes(miniHexPaths);
    drawMiniHexGrid(miniHexPaths);
    return miniHexPaths;
  };

  return { generateMiniHexGrid };
};

/**
 * Mini hex generation based on the [cols, rows] system
 * Explained here:
 * https://www.notion.so/tormentedsouls/Hex-grid-generation-15bc6c3d36fc47ea9e8a2a2fec64d355#1b91ee871f804ec3a1635f51a8adc805 */
const MINI_HEX_MAP_5x5 = [
  [0, 1, 1, 1, 0],
  [1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1],
  [1, 1, 1, 1, 0],
  [0, 1, 1, 1, 0],
];

const MINI_HEX_MAP_7x7 = [
  [0, 0, 1, 1, 0, 0, 0],
  [1, 1, 1, 1, 1, 0, 0],
  [1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 0, 0],
  [1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 0, 0],
  [0, 0, 1, 1, 0, 0, 0],
];
